import React from "react"
import { Page, Text, View, Document } from "@react-pdf/renderer"
import { useTranslation } from "react-i18next"

import { styles } from "./styles"
import HeaderPdf from "./HeaderPdf"
import DataViewPdf from "./DataViewPdf"

import dateService from "services/date"
import { isNotEmpty } from "utils/validation"
import {
	OWNER_PRIVATE_GENDER,
	OWNER_COMPANY_GENDER,
	getLabelFromBikeId,
	getBikeLabelByText,
	bikeBrands,
	bikeTypes,
} from "utils/constants"
import { Bike, Entity } from "models"
import { isVaeBike } from "features/bikeFleet/utils"

type LayoutpdfProps = {
	children: JSX.Element[] | JSX.Element
	title: string
	bike: Bike
	entity: Entity
	operatorGender: string
}

const Layoutpdf: React.FC<LayoutpdfProps> = ({
	children,
	title,
	bike,
	entity,
	operatorGender = null,
}) => {
	const { t } = useTranslation()

	const contents: Array<string> = t("pdf.main_top_content", { ns: "bike", returnObjects: true })
	const activatedBikeDate = bike?.bike_last_registered_by?.create_date
		? dateService.formatDate(bike?.bike_last_registered_by?.create_date, "dd/MM/y")
		: dateService.formatDate(new Date(), "dd/MM/y")

	const entityName = bike?.bike_last_registered_by?.social_reason
		? bike?.bike_last_registered_by?.social_reason
		: entity?.entity_data?.raison_sociale

	let gearTypeFind
	let brandFind

	if (bike?.bike_description?.gear_type_id)
		gearTypeFind =
			bikeTypes.find((b) => b.value === bike?.bike_description?.gear_type_id) || bikeTypes[0]
	if (bike?.bike_description?.brand)
		brandFind =
			bikeBrands.find(
				(b) => b.label.toLowerCase() === bike?.bike_description?.brand.toLowerCase()
			) || bikeBrands[0]

	const gearType =
		gearTypeFind && gearTypeFind.value !== 999
			? gearTypeFind.label
			: bike?.bike_description?.gear_type
	const gearTypeId = gearTypeFind ? gearTypeFind.value : bike?.bike_description?.gear_type_id
	const brandLabel =
		brandFind && brandFind.value !== 999 ? brandFind.label : bike?.bike_description?.brand

	const gearTypeLabel = isNotEmpty(bike.bike_description.gear_type_id)
		? getLabelFromBikeId(gearTypeId)
		: getBikeLabelByText(gearType)
	const isVae = isVaeBike(bike?.bike_description?.is_electric, bike.bike_description.gear_type)

	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<HeaderPdf bike={bike} operatorGender={operatorGender} />

				{isNotEmpty(title) && (
					<View style={styles.sectionTitle}>
						<Text style={styles.h1}>{title}</Text>
					</View>
				)}

				<View style={styles.section}>
					<View>
						<Text style={[styles.h2]}>
							{t("pdf.pdf_identification_title", { ns: "bike" })}
						</Text>
					</View>

					<View>
						<Text style={[styles.p]}>{t("pdf.heading", { ns: "bike" })}</Text>
						<Text style={[styles.p]}>
							{t("pdf.intro", { ns: "bike", entityName: entityName })}
						</Text>
						<Text style={[styles.p]}>
							{t("pdf.intro_2", { ns: "bike", registeredDate: activatedBikeDate })}
						</Text>
						{contents.map((content, index) => {
							return (
								<Text style={styles.p} key={index}>
									{" "}
									{content}{" "}
								</Text>
							)
						})}
					</View>

					<View style={styles.grid}>
						<View style={styles.sectionRight}>
							<Text style={styles.h3}>{t("pdf.bike_info", { ns: "bike" })}</Text>
							<DataViewPdf label={t("brand", { ns: "bike" })} value={brandLabel} />
							<DataViewPdf
								label={t("model", { ns: "bike" })}
								value={bike?.bike_description?.model}
							/>
							<DataViewPdf
								label={t("type", { ns: "bike" })}
								value={t(`${gearTypeLabel}`)}
							/>
							<DataViewPdf
								label={t("color", { ns: "bike" })}
								value={bike?.bike_description?.color}
							/>

							{isVae && (
								<DataViewPdf
									label={t("type_electric", { ns: "bike" })}
									value={"Oui"}
								/>
							)}

							{!isVae && (
								<DataViewPdf
									label={t("type_electric", { ns: "bike" })}
									value={"Non"}
								/>
							)}

							<DataViewPdf label={t("code", { ns: "bike" })} value={bike?.bike_id} />
							{isNotEmpty(bike?.bike_description?.frame_serial_number) && (
								<DataViewPdf
									label={t("serial", { ns: "bike" })}
									value={bike?.bike_description?.frame_serial_number}
								/>
							)}
							{isNotEmpty(bike?.bike_description?.engine_serial_number) && (
								<DataViewPdf
									label={t("serial_engine", { ns: "bike" })}
									value={bike?.bike_description?.engine_serial_number}
								/>
							)}
							{isNotEmpty(bike?.bike_description.battery_serial_number) && (
								<DataViewPdf
									label={t("serial_battery", { ns: "bike" })}
									value={bike?.bike_description.battery_serial_number}
								/>
							)}
						</View>
						<View style={styles.sectionLeft}>
							<Text style={styles.h3}>{t("pdf.personal_info", { ns: "bike" })}</Text>
							{bike?.bike_owner?.gender ? (
								<>
									{bike?.bike_owner?.gender === OWNER_COMPANY_GENDER && (
										<DataViewPdf
											label={t("owner.raison_sociale", { ns: "bike" })}
											value={bike?.bike_owner?.social_reason}
										/>
									)}
									{bike?.bike_owner?.gender === OWNER_PRIVATE_GENDER && (
										<>
											<DataViewPdf
												label={t("owner.name", { ns: "bike" })}
												value={bike?.bike_owner?.last_name}
											/>
											<DataViewPdf
												label={t("owner.firstname", { ns: "bike" })}
												value={bike?.bike_owner?.first_name}
											/>
										</>
									)}
									<DataViewPdf
										label={t("owner.phonenumber", { ns: "bike" })}
										value={bike?.bike_owner?.phone.replace("+33", "0")}
									/>
									<DataViewPdf
										label={t("owner.mail", { ns: "bike" })}
										value={bike?.bike_owner?.mail}
									/>
								</>
							) : (
								<>
									<DataViewPdf
										label={t("owner.mail", { ns: "bike" })}
										value={bike?.bike_owner?.mail}
									/>
								</>
							)}
						</View>
					</View>
				</View>

				{children}
			</Page>
		</Document>
	)
}

export default Layoutpdf
